import { api } from '@invisoutlet/shared/api';
import { useAuthContext } from '@invisoutlet/shared/context';
import { useTheme } from '@invisoutlet/shared/hooks';
import type { Entity } from '@invisoutlet/shared/types';
import { DEFAULT_OCCUPIED_COLOR, DEFAULT_VACANT_COLOR } from '@invisoutlet/shared/utils';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { HexColorPicker } from 'react-colorful';
import { EnterpriseLayout } from '../components/enterprise-layout.js';

// ─── Sub-components ───────────────────────────────────────────────────────────

function ColorInput({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (hex: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState(value);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setText(value);
  }, [value]);

  useEffect(() => {
    if (!open) return;
    function handleOutsideClick(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, [open]);

  function handleTextBlur() {
    if (/^#[0-9a-f]{6}$/i.test(text)) onChange(text.toLowerCase());
    else setText(value);
  }

  return (
    <div className="flex items-center gap-3" ref={containerRef}>
      <span className="w-20 shrink-0 text-sm text-text-secondary">{label}</span>

      <div className="relative">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="h-8 w-8 rounded-lg border border-border shadow-sm transition-transform hover:scale-110 focus:outline-none focus:ring-2 focus:ring-accent/50"
          style={{ backgroundColor: value }}
          aria-label={`${label} — open color picker`}
          aria-expanded={open}
        />
        {open && (
          <div className="absolute left-0 top-10 z-50 rounded-xl border border-border bg-bg-primary p-3 shadow-lg">
            <HexColorPicker
              color={value}
              onChange={(hex) => {
                onChange(hex);
                setText(hex);
              }}
              style={{ width: '200px', height: '160px' }}
            />
          </div>
        )}
      </div>

      <input
        type="text"
        value={text}
        maxLength={7}
        onChange={(e) => setText(e.target.value)}
        onBlur={handleTextBlur}
        className="w-24 rounded-lg border border-border bg-bg-surface px-2 py-1.5 font-mono text-xs text-text-primary focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent/30"
        placeholder="#rrggbb"
        aria-label={`${label} hex value`}
      />
    </div>
  );
}

function SectionCard({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children: React.ReactNode;
}) {
  return (
    <section className="rounded-xl border border-border bg-bg-primary p-5">
      <div className="mb-4">
        <h2 className="text-sm font-semibold text-text-primary">{title}</h2>
        {description && <p className="mt-0.5 text-xs text-text-secondary">{description}</p>}
      </div>
      {children}
    </section>
  );
}

function Toast({ toast }: { toast: { message: string; type: 'success' | 'error' } | null }) {
  return (
    <div
      role="status"
      aria-live="polite"
      aria-atomic="true"
      className={[
        'fixed bottom-6 right-6 z-50 rounded-xl px-4 py-3 text-sm font-medium shadow-lg transition-opacity duration-200',
        toast
          ? `opacity-100 ${toast.type === 'success' ? 'bg-success text-white' : 'bg-danger text-white'}`
          : 'pointer-events-none opacity-0',
      ].join(' ')}
    >
      {toast?.message ?? ''}
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

const THEME_OPTIONS: { value: 'light' | 'dark' | 'system'; label: string }[] = [
  { value: 'light', label: 'Light' },
  { value: 'dark', label: 'Dark' },
  { value: 'system', label: 'System' },
];

const NOTIFICATION_OPTIONS: {
  value: 'auto' | 'manual' | 'off';
  label: string;
  description: string;
}[] = [
  {
    value: 'auto',
    label: 'Auto',
    description: 'Alerts trigger automatically when rules are violated.',
  },
  {
    value: 'manual',
    label: 'Manual',
    description: 'Rules exist but must be manually armed to send alerts.',
  },
  {
    value: 'off',
    label: 'Off',
    description: 'No alerts are sent, even when rules are violated.',
  },
];

export default function SettingsPage() {
  const { user, refreshUser } = useAuthContext();
  const entityId = user!.entityId!;
  const queryClient = useQueryClient();
  const { theme, setTheme } = useTheme();

  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  // Local state for the preferences form
  const [tempUnit, setTempUnit] = useState<'°F' | '°C'>('°F');
  const [notification, setNotification] = useState<'auto' | 'manual' | 'off'>('auto');
  const [occupiedColor, setOccupiedColor] = useState(DEFAULT_OCCUPIED_COLOR);
  const [vacantColor, setVacantColor] = useState(DEFAULT_VACANT_COLOR);
  const [dirty, setDirty] = useState(false);

  // Local state for the name form
  const [nameValue, setNameValue] = useState(user?.name ?? '');
  const nameDirty = nameValue.trim() !== (user?.name ?? '');

  // Entity data
  const { data: entityRes, isLoading } = useQuery({
    queryKey: ['entity', entityId],
    queryFn: () => api.get<Entity>(`/api/entities/${entityId}`),
  });
  const entity = entityRes?.data;

  // Seed local state from entity once loaded
  useEffect(() => {
    if (entity) {
      setTempUnit(entity.preference.temperatureUnit);
      setNotification(entity.preference.notification);
      setOccupiedColor(entity.preference.occupiedColor ?? DEFAULT_OCCUPIED_COLOR);
      setVacantColor(entity.preference.vacantColor ?? DEFAULT_VACANT_COLOR);
      setDirty(false);
    }
  }, [entity]);

  function showToast(message: string, type: 'success' | 'error') {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3_500);
  }

  function handleOccupiedColorChange(hex: string) {
    setOccupiedColor(hex);
    setDirty(true);
  }

  function handleVacantColorChange(hex: string) {
    setVacantColor(hex);
    setDirty(true);
  }

  // Save preferences mutation
  const prefMutation = useMutation({
    mutationFn: () =>
      api.patch(`/api/entities/${entityId}/preferences`, {
        notification,
        temperatureUnit: tempUnit,
        occupiedColor,
        vacantColor,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['entity', entityId] });
      setDirty(false);
      showToast('Preferences saved.', 'success');
    },
    onError: () => {
      showToast('Failed to save preferences. Please try again.', 'error');
    },
  });

  const nameMutation = useMutation({
    mutationFn: () => api.patch('/api/users/me', { name: nameValue.trim() }),
    onSuccess: () => {
      setNameValue(nameValue.trim());
      void refreshUser();
      showToast('Name updated.', 'success');
    },
    onError: () => {
      showToast('Failed to update name. Please try again.', 'error');
    },
  });

  function handleTempUnitChange(unit: '°F' | '°C') {
    setTempUnit(unit);
    setDirty(true);
  }

  function handleNotificationChange(value: 'auto' | 'manual' | 'off') {
    setNotification(value);
    setDirty(true);
  }

  const labelClass = 'text-sm font-medium text-text-primary';

  return (
    <EnterpriseLayout title="Settings">
      <div className="mx-auto max-w-2xl">
        <div className="mb-6">
          <h1 className="text-xl font-bold text-text-primary">Settings</h1>
          <p className="mt-1 text-sm text-text-secondary">
            Manage entity preferences and display options.
          </p>
        </div>

        <div className="space-y-5">
          {/* Entity Preferences */}
          <SectionCard
            title="Entity Preferences"
            description={`Applies to ${entity?.name ?? 'your entity'} — visible to all users.`}
          >
            {isLoading ? (
              <div className="space-y-4">
                {[1, 2].map((n) => (
                  <div key={n} className="h-10 animate-pulse rounded-lg bg-bg-elevated" />
                ))}
              </div>
            ) : (
              <div className="space-y-5">
                {/* Temperature unit */}
                <div>
                  <p className={labelClass}>Temperature unit</p>
                  <div className="mt-2 flex gap-2">
                    {(['°F', '°C'] as const).map((unit) => (
                      <button
                        key={unit}
                        type="button"
                        onClick={() => handleTempUnitChange(unit)}
                        className={[
                          'rounded-lg px-4 py-2 text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-accent',
                          tempUnit === unit
                            ? 'bg-accent text-white'
                            : 'border border-border bg-bg-surface text-text-secondary hover:bg-bg-elevated hover:text-text-primary',
                        ].join(' ')}
                        aria-pressed={tempUnit === unit}
                      >
                        {unit}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Occupancy colors */}
                <div>
                  <p className={labelClass}>Occupancy colors</p>
                  <p className="mb-3 mt-0.5 text-xs text-text-secondary">
                    Card tint shown on room and device cards.
                  </p>
                  <div className="space-y-2">
                    <ColorInput
                      label="Occupied"
                      value={occupiedColor}
                      onChange={handleOccupiedColorChange}
                    />
                    <ColorInput
                      label="Vacant"
                      value={vacantColor}
                      onChange={handleVacantColorChange}
                    />
                  </div>
                </div>

                {/* Notification mode */}
                <div>
                  <p className={labelClass}>Notification mode</p>
                  <div className="mt-2 space-y-2">
                    {NOTIFICATION_OPTIONS.map(({ value, label, description }) => (
                      <label
                        key={value}
                        className={[
                          'flex cursor-pointer items-start gap-3 rounded-lg border p-3 transition-colors',
                          notification === value
                            ? 'border-accent/50 bg-accent/5'
                            : 'border-border hover:bg-bg-surface',
                        ].join(' ')}
                      >
                        <input
                          type="radio"
                          name="notification"
                          value={value}
                          checked={notification === value}
                          onChange={() => handleNotificationChange(value)}
                          className="mt-0.5 accent-accent"
                        />
                        <div>
                          <p className="text-sm font-medium text-text-primary">{label}</p>
                          <p className="text-xs text-text-secondary">{description}</p>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Save button */}
                <div className="flex justify-end border-t border-border pt-4">
                  <button
                    type="button"
                    onClick={() => prefMutation.mutate()}
                    disabled={!dirty || prefMutation.isPending}
                    className="rounded-lg bg-accent px-4 py-2 text-sm font-medium text-white hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-accent/50 disabled:opacity-50"
                  >
                    {prefMutation.isPending ? 'Saving…' : 'Save preferences'}
                  </button>
                </div>
              </div>
            )}
          </SectionCard>

          {/* Display Settings */}
          <SectionCard title="Display" description="Personal display preferences — stored locally.">
            <div>
              <p className={labelClass}>Theme</p>
              <div className="mt-2 flex gap-2">
                {THEME_OPTIONS.map(({ value, label }) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => setTheme(value)}
                    className={[
                      'rounded-lg px-4 py-2 text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-accent',
                      theme === value
                        ? 'bg-accent text-white'
                        : 'border border-border bg-bg-surface text-text-secondary hover:bg-bg-elevated hover:text-text-primary',
                    ].join(' ')}
                    aria-pressed={theme === value}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
          </SectionCard>

          {/* Account info */}
          <SectionCard title="Account">
            <div className="space-y-4">
              {/* Editable name */}
              <div>
                <label htmlFor="account-name" className={labelClass}>
                  Name
                </label>
                <div className="mt-2 flex gap-2">
                  <input
                    id="account-name"
                    type="text"
                    value={nameValue}
                    onChange={(e) => setNameValue(e.target.value)}
                    maxLength={100}
                    className="flex-1 rounded-lg border border-border bg-bg-surface px-3 py-2 text-sm text-text-primary placeholder:text-text-secondary focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent/30"
                    placeholder="Your name"
                  />
                  <button
                    type="button"
                    onClick={() => nameMutation.mutate()}
                    disabled={!nameDirty || nameValue.trim().length === 0 || nameMutation.isPending}
                    className="rounded-lg bg-accent px-4 py-2 text-sm font-medium text-white hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-accent/50 disabled:opacity-50"
                  >
                    {nameMutation.isPending ? 'Saving…' : 'Save'}
                  </button>
                </div>
              </div>

              <dl className="space-y-2 border-t border-border pt-4 text-sm">
                <div className="flex justify-between">
                  <dt className="text-text-secondary">Email</dt>
                  <dd className="font-medium text-text-primary">{user?.email ?? '—'}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-text-secondary">Role</dt>
                  <dd className="font-medium capitalize text-text-primary">{user?.role ?? '—'}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-text-secondary">Access</dt>
                  <dd className="font-medium text-text-primary">
                    {user?.readOnly ? 'Read-only' : user?.selfService ? 'Self-service' : 'Standard'}
                  </dd>
                </div>
              </dl>
            </div>
          </SectionCard>
        </div>
      </div>

      <Toast toast={toast} />
    </EnterpriseLayout>
  );
}
